/** Status bulk-update CSV: sample download, parse, and process */

import {
  mapCsvStatusToSystem,
  isStatusChangeAllowed,
  getStatusMapping,
} from './statusMaps';

export const downloadStatusSampleCsv = (alertFn = window.alert) => {
  const sampleData = `ORDER ID,STATUS,TRACKING NUMBER
3163,Packing Started,
3162,Shipped Domestic,TRK123456789
3161,Shipped International,INT987654321
3160,Delivered,DEL456789123
3159,progress,
3158,Preorder Placed,`;

  const blob = new Blob([sampleData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', 'sample_orders.csv');
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  alertFn('📁 Sample CSV downloaded!\nUse this format for your uploads.\n\nSupported Status Values:\n• Order Placed, Preorder Placed, progress / Production Update, Delay Update\n• Stock Arrived, Packing Started\n• Shipped Domestic, Shipped International\n• Arrived in Destination Country, Out for Delivery, Customs Cleared\n• Delivered, Review Request, Issue Reported, Refund Processed\n\nTracking Number is optional but recommended for shipping statuses.');
};

/**
 * Pure parse of status-update CSV text.
 * @returns {{ rows: Array, error: string|null, ignoredCount?: number, totalDataLines?: number }}
 */
export const parseStatusCsvText = (csvText) => {
  try {
    if (!csvText || csvText.trim().length === 0) {
      return { rows: [], error: '❌ File appears to be empty!' };
    }

    let cleanCsv = csvText;
    if (cleanCsv.charCodeAt(0) === 0xFEFF) {
      cleanCsv = cleanCsv.substring(1);
    }

    const nullByteCount = (cleanCsv.match(/\x00/g) || []).length;
    const totalLength = cleanCsv.length;

    if (nullByteCount > totalLength * 0.1) {
      return {
        rows: [],
        error: '❌ File appears to be binary or severely corrupted!\n\nPlease ensure you\'re uploading a text CSV file.'
      };
    }

    const lines = cleanCsv.split('\n').filter(line => line.trim().length > 0);

    if (lines.length < 2) {
      return { rows: [], error: '❌ CSV must have at least header row and one data row!' };
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, ' '));

    if (headers.some(h => h.length === 0 || h.length > 50)) {
      return { rows: [], error: '❌ Invalid headers detected!\nHeaders should be 1-50 characters long.' };
    }

    const findColumnIndex = (searchTerms) => {
      return headers.findIndex(header => {
        const cleanHeader = header.replace(/[^a-z0-9]/g, '').toLowerCase();
        return searchTerms.some(term => {
          const cleanTerm = term.replace(/[^a-z0-9]/g, '').toLowerCase();
          return header.includes(term.toLowerCase()) ||
            cleanHeader.includes(cleanTerm) ||
            header.toLowerCase().includes(term.toLowerCase()) ||
            cleanHeader === cleanTerm;
        });
      });
    };

    const orderIdIndex = findColumnIndex([
      'order id', 'orderid', 'order_id', 'orderno', 'order_no', 'order no',
      'id', 'order', 'order number', 'ordernumber'
    ]);
    const statusIndex = findColumnIndex([
      'status', 'state', 'condition', 'order status', 'orderstatus'
    ]);
    const trackingIndex = findColumnIndex([
      'tracking number', 'tracking', 'track', 'trackingno', 'tracking_no',
      'trackingnumber', 'tracking_number', 'track number', 'tracknumber'
    ]);
    const awbDomesticIndex = findColumnIndex([
      'awbdomestic', 'awb domestic', 'awb_domestic', 'domestic awb', 'domesticawb',
      'domestic tracking', 'domestic_tracking', 'domestictracking'
    ]);
    const lmInternationalIndex = findColumnIndex([
      'lminternational', 'lm international', 'lm_international', 'lastmile', 'last mile',
      'international lm', 'international tracking', 'international_tracking', 'internationaltracking'
    ]);

    if (orderIdIndex === -1) {
      return {
        rows: [],
        error: `❌ Order ID column not found!\n\nYour CSV headers: ${headers.join(', ')}\n\nSupported Order ID names:\n• ORDER ID, Order ID, order id\n• OrderID, orderid, ORDERID\n• Order_ID, order_id, ORDER_ID\n• OrderNo, Order_No, ORDERNO\n• ID, id, Id`
      };
    }

    if (statusIndex === -1) {
      return {
        rows: [],
        error: `❌ Status column not found!\n\nYour CSV headers: ${headers.join(', ')}\n\nSupported Status names:\n• STATUS, Status, status\n• State, STATE, state\n• Condition, CONDITION`
      };
    }

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim());

        const row = {
          orderId: values[orderIdIndex] || '',
          status: (values[statusIndex] || '').toLowerCase(),
          trackingNumber: trackingIndex !== -1 ? (values[trackingIndex] || '') : '',
          awbDomestic: awbDomesticIndex !== -1 ? (values[awbDomesticIndex] || '') : '',
          lmInternational: lmInternationalIndex !== -1 ? (values[lmInternationalIndex] || '') : ''
        };

        if (row.orderId && row.status) {
          data.push(row);
        }
      }
    }

    return {
      rows: data,
      error: null,
      ignoredCount: lines.length - 1 - data.length,
      totalDataLines: lines.length - 1
    };
  } catch (error) {
    console.error('CSV parsing error:', error);
    return {
      rows: [],
      error: '❌ Error parsing CSV file!\n\nPlease check:\n• File is properly formatted\n• No special characters\n• Saved as CSV UTF-8'
    };
  }
};

/** Normalize order refs so #5119 / 5119 / " 5119 " match. */
export const normalizeOrderRef = (value) =>
  String(value || '')
    .trim()
    .replace(/^#/, '')
    .toLowerCase();

/** Exact match on order name (#optional) or Shopify id — avoids partial includes false-positives. */
export const orderMatchesCsvRef = (order, orderId) => {
  const ref = normalizeOrderRef(orderId);
  if (!ref || !order) return false;
  const name = normalizeOrderRef(order.name || order.order_number || '');
  const id = String(order.id ?? order.shopify_id ?? '').trim();
  return name === ref || id === ref || id === String(orderId).trim();
};

/**
 * Process parsed status CSV rows against current orders.
 * Falls back to resolveOrders(orderId) when not in the loaded page.
 * @returns {Promise<object>} results summary
 */
export const processStatusCsvData = async ({
  rows,
  orders,
  updateOrderStatus,
  resolveOrders = null,
  mapCsvStatusToSystem: mapStatus = mapCsvStatusToSystem,
  isStatusChangeAllowed: canChange = isStatusChangeAllowed,
  getStatusMapping: getMapping = getStatusMapping,
  alert: alertFn = window.alert,
}) => {
  const results = {
    total: rows.length,
    success: 0,
    failed: 0,
    notFound: 0,
    details: []
  };

  for (const row of rows) {
    try {
      let matchingOrders = (orders || []).filter((order) => orderMatchesCsvRef(order, row.orderId));

      // CSV rows often reference orders outside the currently loaded page/filter.
      if (matchingOrders.length === 0 && typeof resolveOrders === 'function') {
        try {
          const resolved = await resolveOrders(row.orderId);
          matchingOrders = (resolved || []).filter((order) => orderMatchesCsvRef(order, row.orderId));
        } catch (resolveErr) {
          console.error(`CSV order resolve failed for ${row.orderId}:`, resolveErr);
        }
      }

      if (matchingOrders.length === 0) {
        results.notFound++;
        results.details.push({
          orderId: row.orderId,
          status: 'Not Found',
          message: 'Order not found in system'
        });
        continue;
      }

      const systemStatus = mapStatus(row.status);
      if (!systemStatus) {
        results.failed++;
        results.details.push({
          orderId: row.orderId,
          status: 'Failed',
          message: `Invalid status: ${row.status}`
        });
        continue;
      }

      let successCount = 0;
      let failedCount = 0;

      for (const matchingOrder of matchingOrders) {
        try {
          if (!canChange(matchingOrder.custom_status, systemStatus, matchingOrder.is_preorder)) {
            const statusMapping = getMapping(matchingOrder.is_preorder);
            const why = `Cannot change ${statusMapping[matchingOrder.custom_status] || matchingOrder.custom_status} → ${statusMapping[systemStatus] || systemStatus}${!matchingOrder.is_preorder && ['progress', 'delay', 'preorder_received', 'stock_in'].includes(systemStatus) ? ' (preorder-only status)' : ''}`;
            failedCount++;
            results.details.push({
              orderId: matchingOrder.name || row.orderId,
              status: 'Failed',
              message: why
            });
            continue;
          }

          let selectedTracking = row.trackingNumber || '';
          if (systemStatus === 'shipping_domestic') {
            selectedTracking = row.awbDomestic || row.trackingNumber || '';
          } else if (systemStatus === 'shipping_international') {
            selectedTracking = row.lmInternational || row.awbDomestic || row.trackingNumber || '';
          } else if (systemStatus === 'destoryed' || systemStatus === 'arrived_at_destination' || systemStatus === 'out_of_delivery') {
            selectedTracking = row.lmInternational || row.awbDomestic || row.trackingNumber || '';
          } else if (systemStatus === 'delivered') {
            selectedTracking = row.lmInternational || row.awbDomestic || row.trackingNumber || '';
          }

          const updateResult = await updateOrderStatus(
            matchingOrder.id,
            systemStatus,
            selectedTracking,
            matchingOrder,
          );
          if (updateResult && updateResult.success !== false) {
            successCount++;
          } else {
            console.error(`❌ Failed to update ${matchingOrder.name}:`, updateResult);
            failedCount++;
          }
        } catch (updateError) {
          console.error(`❌ Failed to update ${matchingOrder.name}:`, updateError);
          failedCount++;
        }
      }

      if (successCount > 0) {
        results.success += successCount;
        results.details.push({
          orderId: row.orderId,
          status: 'Success',
          message: `Updated ${successCount} order(s) to ${systemStatus}${(row.lmInternational || row.awbDomestic || row.trackingNumber) ? ' with tracking: ' + (row.lmInternational || row.awbDomestic || row.trackingNumber) : ''}${failedCount > 0 ? ` (${failedCount} failed)` : ''}`
        });
      }

      if (failedCount > 0 && successCount === 0) {
        results.failed++;
        results.details.push({
          orderId: row.orderId,
          status: 'Failed',
          message: `All ${matchingOrders.length} matching orders failed to update`
        });
      }
    } catch (error) {
      results.failed++;
      results.details.push({
        orderId: row.orderId,
        status: 'Error',
        message: error.message
      });
    }
  }

  alertFn(`📊 CSV Processing Complete!\n✅ Success: ${results.success}\n❌ Failed: ${results.failed}\n🔍 Not Found: ${results.notFound}\n📁 Total: ${results.total}`);
  return results;
};
